'use client';

import { HpUserInteraction } from '@zg-rentals/ts-hotpads-api-client';
import { useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { usePathname } from '@/src/i18n/navigation';
import { useCurrentUser, useUserListings } from '@/src/lib/hooks/query';
import type { PropertyDetailListing } from '@/src/components/features/property/types';
import { ListingCard } from '@/src/components/ListingCard';
import { SAVE_CLICKSTREAM_CONTEXTS } from '@/src/components/features/listing-actions/listingActionUtils';
import { CAROUSEL_CARD_IMAGE_SIZES, ListingCarousel } from '@/src/components/ListingCarousel';
import { ListingCarouselPlaceholder } from '@/src/components/ListingCarousel/ListingCarouselPlaceholder';
import { carouselItem } from '@/src/components/ListingCarousel/listingCarousel.styles';
import { container } from './recentlyViewedModule.styles';
import { selectRecentlyViewedListings } from './recentlyViewedModule.utils';

type Props = {
  listing: PropertyDetailListing;
};

export const RecentlyViewedModule = ({ listing }: Props) => {
  const t = useTranslations('hdp');
  const pathname = usePathname();
  const { data: currentUser } = useCurrentUser();
  const { data, isLoading, error } = useUserListings(HpUserInteraction.VIEW, {
    enabled: currentUser?.loggedIn === true,
  });
  const currentListingUri = pathname || listing.uriMalone || listing.uriBuilding || '';

  const filteredListings = useMemo(
    () => selectRecentlyViewedListings(data?.listings, currentListingUri),
    [currentListingUri, data?.listings],
  );

  if (isLoading) {
    return (
      <div role="status" aria-label={t('loadingRecentlyViewed')} aria-busy="true">
        <ListingCarouselPlaceholder />
      </div>
    );
  }

  if (error || filteredListings.length === 0) {
    return null;
  }

  return (
    <div className={container}>
      <ListingCarousel aria-label={t('recentlyViewedHomes')}>
        {filteredListings.map((listing) => (
          <div
            key={listing.aliasEncoded ?? (listing as { uriV2?: string }).uriV2 ?? listing.uriMalone}
            className={carouselItem}
          >
            <ListingCard
              clickstreamTriggerLocation={'recently_viewed_listings_module'}
              saveClickstreamContext={SAVE_CLICKSTREAM_CONTEXTS.recentlyViewed}
              listing={listing}
              sizes={CAROUSEL_CARD_IMAGE_SIZES}
              hideListingActions
            />
          </div>
        ))}
      </ListingCarousel>
    </div>
  );
};
